/**
 * The newest PageSpeed result: the two performance scores in Lighthouse's
 * colour bands, and the six lab metrics side by side for mobile and desktop.
 *
 * Each metric is coloured from its own stored audit score, never from fixed
 * thresholds: Lighthouse judges a desktop metric against desktop limits, and
 * mobile limits would paint every desktop figure green.
 */
import {
  SPEED_METRICS,
  bandColor,
  formatMetric,
  metricBand,
  metricValue,
  scoreBand,
  type SpeedLatestView,
  type SpeedMetricKey,
  type SpeedRunView,
  type SpeedStrategy,
} from "../../../../lib/monitoringSpeed";
import type { T } from "../words";

function ScoreTile({ run, strategy, t }: { run: SpeedRunView | null; strategy: SpeedStrategy; t: T }) {
  const band = run ? scoreBand(run.score) : null;
  return (
    <div className="crm-pstat">
      <b className="crm-tnum" style={{ color: bandColor(band) }}>
        {run ? run.score : "-"}
      </b>
      <span>{t(`monitoring.speed.strategy.${strategy}`)}</span>
      {/* The band in words too: colour alone is lost on a bad screen and to colour blindness. */}
      <span className="crm-pstat-sub">{band ? t(`monitoring.speed.band.${band}`) : t("monitoring.speed.noScore")}</span>
    </div>
  );
}

function MetricCell({ run, metric, t }: { run: SpeedRunView | null; metric: SpeedMetricKey; t: T }) {
  if (!run) return <td className="crm-mut">-</td>;
  const value = metricValue(run.metrics, metric);
  const band = value === null ? null : metricBand(run.metricScores[metric]);
  return (
    <td className="crm-tnum">
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <i aria-hidden className="inline-block size-2 shrink-0 rounded-full" style={{ background: bandColor(band) }} />
        {formatMetric(metric, value)}
        {band ? <span className="sr-only">{t(`monitoring.speed.band.${band}`)}</span> : null}
      </span>
    </td>
  );
}

export function SpeedScores({ latest, t }: { latest: SpeedLatestView; t: T }) {
  return (
    <>
      <div className="crm-pstats grid-cols-2">
        <ScoreTile run={latest.mobile} strategy="mobile" t={t} />
        <ScoreTile run={latest.desktop} strategy="desktop" t={t} />
      </div>
      <div className="overflow-x-auto px-[26px] pt-4">
        <table className="crm-table">
          <thead>
            <tr>
              <th scope="col">{t("monitoring.speed.metricCol")}</th>
              <th scope="col">{t("monitoring.speed.strategy.mobile")}</th>
              <th scope="col">{t("monitoring.speed.strategy.desktop")}</th>
            </tr>
          </thead>
          <tbody>
            {SPEED_METRICS.map((metric) => (
              <tr key={metric}>
                <td>{t(`monitoring.speed.metric.${metric}`)}</td>
                <MetricCell run={latest.mobile} metric={metric} t={t} />
                <MetricCell run={latest.desktop} metric={metric} t={t} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="crm-mut px-[26px] pt-2 text-[12px]">{t("monitoring.speed.legend")}</p>
    </>
  );
}
